import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Circle, Save } from "lucide-react";
import { Field } from "./shared";

// Checklist de conformité d'un contrat (contrat signé déduit automatiquement)
export function ContractCompliance({ contract, catalog, onChanged }) {
  const c = contract;
  const comp = c.compliance || {};
  const [items, setItems] = useState({});
  const [dates, setDates] = useState({ dpae_date: "", visite_medicale_date: "", visite_medicale_prochaine: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setItems(Object.fromEntries(c.compliance_state.items.filter((i) => !i.auto).map((i) => [i.key, i.done])));
    setDates({ dpae_date: comp.dpae_date || "", visite_medicale_date: comp.visite_medicale_date || "", visite_medicale_prochaine: comp.visite_medicale_prochaine || "", notes: comp.notes || "" });
  }, [c.id, c.updated_at, comp.updated_at, comp.dpae_date]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const body = { items, ...Object.fromEntries(Object.entries(dates).map(([k, v]) => [k, v || null])), notes: dates.notes };
      const { data } = await api.put(`/chef/contrats/${c.id}/compliance`, body);
      toast.success("Conformité mise à jour"); onChanged(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const done = c.compliance_state.items.filter((i) => (i.auto ? i.done : items[i.key])).length;

  return (
    <div className="space-y-4" data-testid="ct-compliance">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-[#F1F3F5]"><div className={`h-2 ${done === c.compliance_state.total ? "bg-[#059669]" : "bg-[#FFD700]"}`} style={{ width: `${(100 * done) / c.compliance_state.total}%` }} /></div>
        <span className="font-mono text-sm font-bold" data-testid="ct-compliance-score">{done}/{c.compliance_state.total}</span>
      </div>
      <div className="space-y-1.5" data-testid="ct-compliance-items">
        {c.compliance_state.items.map((it) => {
          const on = it.auto ? it.done : !!items[it.key];
          return (
            <button type="button" key={it.key} disabled={it.auto} onClick={() => setItems({ ...items, [it.key]: !items[it.key] })} data-testid={`ct-check-${it.key}`} aria-pressed={on}
              className={`w-full text-left flex items-start gap-2 p-2.5 border transition-colors ${on ? "border-[#059669]/50 bg-[#ECFDF5]" : "border-[#DEE2E6] hover:border-[#0A0A0A]"} ${it.auto ? "cursor-default" : ""}`}>
              {on ? <CheckCircle2 className="h-4 w-4 text-[#059669] mt-0.5 shrink-0" /> : <Circle className="h-4 w-4 text-[#868E96] mt-0.5 shrink-0" />}
              <div className="text-sm"><div className={on ? "" : "font-semibold"}>{it.label}</div>{it.auto && <div className="text-[10px] text-[#868E96]">Coché automatiquement dès qu'une date de signature ou un scan signé est enregistré (onglets Contrat / Documents).</div>}</div>
            </button>
          );
        })}
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="DPAE transmise le"><Input type="date" value={dates.dpae_date} onChange={(e) => setDates({ ...dates, dpae_date: e.target.value })} data-testid="ct-dpae-date" className="h-10" /></Field>
        <Field label="Dernière visite médicale"><Input type="date" value={dates.visite_medicale_date} onChange={(e) => setDates({ ...dates, visite_medicale_date: e.target.value })} data-testid="ct-visite-date" className="h-10" /></Field>
        <Field label="Prochaine visite (rappel J-30 / J-7)"><Input type="date" value={dates.visite_medicale_prochaine} onChange={(e) => setDates({ ...dates, visite_medicale_prochaine: e.target.value })} data-testid="ct-visite-next" className="h-10" /></Field>
      </div>
      <Field label="Notes internes"><Textarea value={dates.notes} onChange={(e) => setDates({ ...dates, notes: e.target.value })} rows={2} data-testid="ct-compliance-notes" /></Field>
      <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="ct-compliance-save" className="bg-[#002FA7] hover:bg-[#002175] text-white"><Save className="h-4 w-4 mr-2" />{saving ? "Enregistrement…" : "Enregistrer la conformité"}</Button></div>
    </div>
  );
}
